// =============================================================================
// VandieZ admin-web -- Dispute queue (/disputes)
// =============================================================================
// Lists open + under_review disputes by default (the action queue).
// Status filter lets the admin pivot to resolved / rejected for audit.
// Click a row to open the detail page where the actual resolution
// happens.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Scale,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { disputesAdminApi, streamCsvDownload } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminDisputeQueueEntry,
  DisputeStatus,
} from "@/types/disputes";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

const STATUS_FILTERS: { label: string; value: DisputeStatus | "queue" }[] =
  [
    { label: "Queue (open + review)", value: "queue" },
    { label: "Resolved", value: "resolved" },
    { label: "Rejected", value: "rejected" },
  ];

const STATUS_PILL: Record<DisputeStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  under_review: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-muted text-muted-foreground",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function DisputesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  // URL holds the filter + offset so the operator can bookmark or share.
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<AdminDisputeQueueEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportingAll, setExportingAll] = useState<boolean>(false);
  const toast = useToast();

  const filter = (searchParams.get("filter") as DisputeStatus | "queue" | null) ?? "queue";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;
  const sizeRaw = Number.parseInt(searchParams.get("size") ?? "", 10);
  const pageSize = PAGE_SIZE_OPTIONS.includes(
    sizeRaw as (typeof PAGE_SIZE_OPTIONS)[number],
  )
    ? sizeRaw
    : DEFAULT_PAGE_SIZE;

  const updateFilters = useCallback(
    (next: { filter?: string; offset?: number; size?: number }): void => {
      setSearchParams(
        (cur) => {
          const params = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (
              v === undefined ||
              v === "" ||
              v === "queue" ||
              v === 0 ||
              (k === "size" && v === DEFAULT_PAGE_SIZE)
            ) {
              params.delete(k);
            } else {
              params.set(k, String(v));
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await disputesAdminApi.queue(
          accessToken,
          {
            limit: pageSize,
            offset,
            ...(filter !== "queue" ? { status: filter } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filter, offset, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPage]);

  const exportAllCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExportingAll(true);
    try {
      const qsParts: string[] = [];
      if (filter !== "queue") {
        qsParts.push(`status=${encodeURIComponent(filter)}`);
      }
      const suffix = qsParts.length > 0 ? `?${qsParts.join("&")}` : "";
      const stamp = new Date().toISOString().slice(0, 10);
      await streamCsvDownload(
        accessToken,
        `/admin/exports/disputes.csv${suffix}`,
        `vandiez-disputes-${stamp}.csv`,
      );
      toast.success("Export started — check your downloads.");
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Export failed.";
      toast.error(msg);
    } finally {
      setExportingAll(false);
    }
  };

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const res = await disputesAdminApi.queue(accessToken, {
        limit: 200,
        offset: 0,
        ...(filter !== "queue" ? { status: filter } : {}),
      });
      const header = [
        "id",
        "trip_id",
        "raised_by_user_id",
        "raised_by_role",
        "category",
        "status",
        "raised_at",
      ];
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [
        header.join(","),
        ...res.items.map((r) =>
          [
            r.id,
            r.trip_id,
            r.raised_by_user_id,
            r.raised_by_role,
            r.category,
            r.status,
            r.raised_at,
          ]
            .map((c) => escape(String(c)))
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-disputes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dispute queue"
        description="Open + under-review disputes awaiting resolution. Oldest first."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void exportCsv()}
              disabled={exporting || items.length === 0}
              title="First 200 rows of the current view"
            >
              <Download className="mr-1 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button
              variant="outline"
              onClick={() => void exportAllCsv()}
              disabled={exportingAll}
              title="Stream every row matching the current filter (unlimited)"
            >
              <Download className="mr-1 h-4 w-4" />
              {exportingAll ? "Streaming…" : "Export all"}
            </Button>
          </div>
        }
      />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" /> Disputes
            </CardTitle>
            <CardDescription>
              Oldest open first. Filter at the top to flip between active
              queue and the resolved / rejected audit.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => {
                    updateFilters({ filter: f.value, offset: 0 });
                  }}
                  className={cn(
                    "rounded px-3 py-1 text-sm",
                    filter === f.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={6} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                Nothing in this view right now.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Raised at</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Raised by</th>
                      <th className="py-2 pr-4">Trip</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                        onClick={() => {
                          navigate(`/disputes/${row.id}`);
                        }}
                      >
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {formatDate(row.raised_at)}
                        </td>
                        <td className="py-3 pr-4 capitalize">
                          {row.category.replace(/_/g, " ")}
                        </td>
                        <td className="py-3 pr-4 capitalize">
                          {row.raised_by_role}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          <Link
                            to={`/trips/${row.trip_id}`}
                            className="hover:underline"
                            onClick={(e) => { e.stopPropagation(); }}
                          >
                            {row.trip_id.slice(0, 8)}…
                          </Link>
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                              STATUS_PILL[row.status],
                            )}
                          >
                            {row.status.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <Button variant="link" size="sm" className="px-0">
                            Review →
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => { updateFilters({ offset: Math.max(0, offset - pageSize) }); }}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      updateFilters({
                        size: Number.parseInt(e.target.value, 10),
                        offset: 0,
                      });
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => { updateFilters({ offset: offset + pageSize }); }}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
